import { Link, useNavigate } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';

interface Contact {
  id: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface Props {
  contact: Contact | null;
  active?: 'home' | 'tickets' | 'kb' | 'reports' | 'billing' | 'team' | 'projects';
}

export function PortalNavBar({ contact, active }: Props): React.ReactElement {
  const navigate = useNavigate();

  async function logout() {
    try {
      await apiFetch('/api/portal/auth/logout', { method: 'POST' });
    } catch (err) {
      if (err instanceof ApiError && err.status !== 401) {
        // best effort
      }
    } finally {
      navigate('/login', { replace: true });
    }
  }

  const navLink = (key: NonNullable<Props['active']>) =>
    `text-sm font-medium transition ${
      active === key ? 'text-[var(--color-header)]' : 'text-[var(--color-muted)] hover:text-[var(--color-header)]'
    }`;

  return (
    <header className="border-b border-[var(--color-border)] bg-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4 sm:px-8">
        <Link to="/portal" className="flex items-center gap-3">
          <img src="/t3-logo-nav.png" alt="T3 Tech Partners" className="h-9 w-auto" />
        </Link>
        <nav className="hidden items-center gap-7 lg:flex">
          <Link to="/portal" className={navLink('home')}>Help center</Link>
          <Link to="/portal/tickets" className={navLink('tickets')}>My tickets</Link>
          <Link to="/portal/projects" className={navLink('projects')}>Projects</Link>
          <Link to="/portal/kb" className={navLink('kb')}>Knowledge base</Link>
          <Link to="/portal/reports" className={navLink('reports')}>Hours &amp; Reports</Link>
          {contact?.isOrgAdmin && (
            <Link to="/portal/team" className={navLink('team')}>Team</Link>
          )}
          {contact?.isOrgBilling && (
            <Link to="/portal/billing" className={navLink('billing')}>Billing</Link>
          )}
        </nav>
        <div className="flex items-center gap-3">
          {contact && (
            <button
              type="button"
              onClick={() => void logout()}
              className="hidden text-sm font-medium text-[var(--color-muted)] hover:text-[var(--color-header)] sm:inline"
            >
              Sign out
            </button>
          )}
          <Link
            to="/portal/tickets/new"
            className="rounded-full bg-[var(--color-header)] px-5 py-2 text-sm font-semibold text-white hover:bg-black"
          >
            + Submit a request
          </Link>
        </div>
      </div>
    </header>
  );
}
